import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { makeStyles } from "@material-ui/core";
import Loading from "../../../../common-components/Loading";
import {
  getIndirectVersionsAPI,
  getIndirectLaborTableAPI,
  // getIndirectLaborBudgetTableAPI, // disabled for now — all versions go through /labor/table
  downloadEmployeeLaborAPI,
} from "../../../../api/reports-api";
import { withPeriodFiltersForDownload } from "../filterRelationshipUtils";
import {
  applyNumFilter,
  isNumFilterActive,
  calcOpDropdownPos,
} from "../pl-by-program/plTableFilterUtils";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";

import {
  TABLE_FONT,
  TH_BG,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  TOTAL_BG_ALT,
  ACCENT_COLOR,
  TABLE_BORDER,
  CELL_FG_MUTED,
  CELL_FG_STRONG,
  TH_BG_BAND,
  TH_TEXT,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../../reportTableConfig";

const HEADER_BG = TH_BG;
const HEADER_COL = TH_BG;
const ACCENT = ACCENT_COLOR;
const ACCENT2    = "#EEF2FF";

const MONTHS_LOWER = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const EL_MONTHS    = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const QTR_MONTHS = { "1":["Jan","Feb","Mar"], "2":["Apr","May","Jun"], "3":["Jul","Aug","Sep"], "4":["Oct","Nov","Dec"] };
const MONTH_ALIAS = {
  january:"Jan", jan:"Jan", february:"Feb", feb:"Feb", march:"Mar", mar:"Mar",
  april:"Apr",   apr:"Apr", may:"May",       june:"Jun", jun:"Jun",
  july:"Jul",    jul:"Jul", august:"Aug",    aug:"Aug",  september:"Sep", sep:"Sep",
  october:"Oct", oct:"Oct", november:"Nov",  nov:"Nov",  december:"Dec",  dec:"Dec",
};
function qtrToMonths(q) { return QTR_MONTHS[String(q).replace(/^Q/i, "")] || []; }
function normMonth(m)   { return MONTH_ALIAS[String(m).toLowerCase()] ?? m; }

/* Column visibility: only quarter filter drives which columns show */
function deriveColMonths(filters) {
  if (filters.monthName?.length) {
    return new Set(filters.monthName.map(normMonth)); // show only selected months
  }
  if (filters.quarter?.length) {
    const s = new Set();
    filters.quarter.forEach((q) => qtrToMonths(q).forEach((m) => s.add(m)));
    if (s.size) return s;
  }
  return null; // show all columns
}

/* Total calculation: monthName filter first, then quarter, then all */
function deriveTotalMonths(filters) {
  if (filters.monthName?.length) {
    return new Set(filters.monthName.map(normMonth));
  }
  if (filters.quarter?.length) {
    const s = new Set();
    filters.quarter.forEach((q) => qtrToMonths(q).forEach((m) => s.add(m)));
    if (s.size) return s;
  }
  return null; // sum all months
}

function normalizeVersions(data = []) {
  return data
    .map((v) => {
      if (typeof v === "object" && v !== null) {
        const id = v.key ?? v.id ?? v.version ?? v.versionId ?? v.version_id;
        const label = v.name ?? v.version_name ?? v.versionName ?? String(id);
        return { id, label };
      }
      return { id: v, label: String(v) };
    })
    .filter((v) => v.id != null);
}
function extractRows(res) {
  if (!res) return [];
  if (Array.isArray(res?.data?.rows)) return res.data.rows;
  if (Array.isArray(res?.data))       return res.data;
  if (Array.isArray(res?.rows))       return res.rows;
  if (Array.isArray(res))             return res;
  return [];
}

const fmtEL  = (v) => {
  if (v === null || v === undefined) return "";
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `($${abs})` : `$${abs}`;
};

const OPS = [
  { key: "gt",  label: ">",  title: "Greater than" },
  { key: "gte", label: "≥",  title: "Greater than or equal" },
  { key: "lt",  label: "<",  title: "Less than" },
  { key: "lte", label: "≤",  title: "Less than or equal" },
  { key: "eq",  label: "=",  title: "Equal to" },
  { key: "neq", label: "≠",  title: "Not equal to" },
];

function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function ColsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="18" /><rect x="14" y="3" width="7" height="18" />
    </svg>
  );
}

const useStyles = makeStyles(() => ({
  svbFilters: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  tableCard: {
    background: "#FFFFFF",
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.08)",
    marginBottom: 20,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "visible",
  },
  tableWrap: {
    overflow: "auto",
    maxHeight: "calc(100vh - 220px)",
    background: "#FFFFFF",
  },
  tableBody: {
    overflow: "hidden",
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  svbFilterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": { borderColor: "#C7D2FE", boxShadow: "0 3px 10px rgba(74,85,162,0.12)" },
    "&:focus-within": { borderColor: "#A5B4FC", boxShadow: "0 0 0 3px rgba(240,242,255,0.9)" },
  },
  svbFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: "'IBM Plex Sans', sans-serif",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  svbFilterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 130,
    height: "100%",
    fontFamily: "'IBM Plex Sans', sans-serif",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
  colPickerWrap: { position: "relative" },
  colsBtn: { display: "inline-flex", alignItems: "center", gap: 6, height: 36, padding: "0 12px", borderRadius: 6, border: "1px solid #E2E8F0", background: "#fff", fontSize: 12, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "'IBM Plex Sans', sans-serif", whiteSpace: "nowrap", "&:hover": { background: "#F9FAFB", borderColor: "#C7D2FE" } },
  colsBtnActive: { borderColor: "#C7D2FE", background: ACCENT2, color: ACCENT },
  hiddenBadge: { display: "inline-flex", alignItems: "center", height: 18, padding: "0 7px", borderRadius: 10, background: ACCENT, color: "#fff", fontSize: 11, fontWeight: 600, lineHeight: 1 },
  colPicker: { position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 1200, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.12)", minWidth: 180, maxHeight: 420, overflowY: "auto", fontFamily: "'IBM Plex Sans', sans-serif" },
  colPickerHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 8px", borderBottom: "1px solid #F3F4F6", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  colPickerTitle: { fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.05em", textTransform: "uppercase" },
  colPickerReset: { fontSize: 11, fontWeight: 600, color: ACCENT, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "'IBM Plex Sans', sans-serif", "&:hover": { textDecoration: "underline" } },
  colPickerSection: { fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", padding: "8px 14px 4px" },
  colPickerItem: { display: "flex", alignItems: "center", gap: 9, padding: "7px 14px", cursor: "pointer", fontSize: 12, color: "#374151", userSelect: "none", borderBottom: "1px solid #F9FAFB", "&:last-child": { borderBottom: "none" }, "&:hover": { background: "#F9FAFB" } },
  colPickerCheck: { width: 14, height: 14, accentColor: ACCENT, cursor: "pointer", flexShrink: 0 },
  filterIconBtn: { flexShrink: 0, width: 16, height: 16, border: "none", background: "transparent", cursor: "pointer", padding: 0, borderRadius: 3, color: "#fff", opacity: 0.45, display: "inline-flex", alignItems: "center", justifyContent: "center", "&:hover": { opacity: 1, background: "rgba(255,255,255,0.18)" } },
  filterIconBtnActive: { opacity: 1, color: "#A5B4FC", background: "rgba(165,180,252,0.22)", borderRadius: 3 },
  filterPanel: { position: "fixed", zIndex: 99, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "10px 12px", minWidth: 180, fontFamily: FONT_EL },
  filterTextInput: { display: "block", width: "100%", boxSizing: "border-box", height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5, fontSize: 11, padding: "0 8px", outline: "none", fontFamily: FONT_EL, background: "#fff", color: "#111827", "&:focus": { borderColor: ACCENT }, "&::placeholder": { color: "#9CA3AF" } },
  filterNumRow: { display: "flex", alignItems: "center", gap: 3, height: 26 },
  filterNumInput: { width: "100%", height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5, fontSize: 11, textAlign: "right", padding: "0 6px", outline: "none", fontFamily: FONT_EL, background: "#fff", color: "#111827", "&:focus": { borderColor: ACCENT }, "&::placeholder": { color: "#9CA3AF" } },
  filterClearBtn: { flexShrink: 0, width: 18, height: 18, border: "none", borderRadius: "50%", background: "#E2E8F0", fontSize: 9, color: "#64748B", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, "&:hover": { background: "#CBD5E1" } },
  opBtn: { flexShrink: 0, width: 22, height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5, background: "#fff", fontSize: 11, fontWeight: 700, color: ACCENT, cursor: "pointer", fontFamily: FONT_EL, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, "&:hover": { borderColor: ACCENT, background: ACCENT2 } },
  opBtnActive: { background: ACCENT, borderColor: ACCENT, color: "#fff", "&:hover": { background: "#4338CA" } },
  opDropdown: { position: "fixed", zIndex: 99, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 190, padding: "4px 0", fontFamily: FONT_EL },
  opOption: { display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 14px", background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#374151", fontFamily: FONT_EL, textAlign: "left", "&:hover": { background: "#F9FAFB" } },
  opOptionActive: { background: ACCENT2, color: ACCENT, fontWeight: 600, "&:hover": { background: ACCENT2 } },
  opSymbol: { fontWeight: 700, fontSize: 15, color: ACCENT, width: 18, textAlign: "center", flexShrink: 0 },
  opTitle: { fontSize: 12 },
  clearFiltersBtn: { display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 10px", borderRadius: 6, border: "1px solid #FCA5A5", background: "#FFF1F2", fontSize: 12, fontWeight: 500, color: "#DC2626", cursor: "pointer", fontFamily: FONT_EL, whiteSpace: "nowrap", "&:hover": { background: "#FEE2E2" } },
}));

/* ── Inline table styles ── */
const FONT_EL  = "'IBM Plex Sans', sans-serif";

const TOTAL_FG = CELL_FG_STRONG;

const TOTAL_BG2 = TOTAL_BG_ALT;
const NAME_W   = 200;
const COL_W_EL = 96;
const Z_H = 6, Z_S = 4, GH_EL = 36, CH_EL = 38, RH_EL = 44;
const TH_SH_G_EL = "inset 0 -1px 0 #4a4a4c";
const TH_SH_EL   = "inset 0 -1px 0 #4a4a4c";

const thCoreEL = (top, h, z, pad, bg) => ({
  position: "sticky", top, zIndex: z, height: h, lineHeight: 1.25,
  boxSizing: "border-box", background: bg, color: TH_TEXT,
  verticalAlign: "middle", padding: pad, whiteSpace: "nowrap",
  borderRight: "1px solid #4a4a4c", borderBottom: "none",
});
const thFixedBlankEL = () => ({
  ...thCoreEL(0, GH_EL, Z_H + 1, 0, HEADER_BG),
  left: 0, width: NAME_W, minWidth: NAME_W, maxWidth: NAME_W, boxShadow: TH_SH_G_EL,
});
const thBandGroupEL = () => ({
  ...thCoreEL(0, GH_EL, Z_H, "7px 14px", HEADER_BG),
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  textTransform: "uppercase", textAlign: "center", boxShadow: TH_SH_G_EL,
});
const thFixedColEL = () => ({
  ...thCoreEL(GH_EL, CH_EL, Z_H + 1, "7px 14px", HEADER_COL),
  left: 0, width: NAME_W, minWidth: NAME_W, maxWidth: NAME_W,
  fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", textAlign: "left",
  boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_EL}`,
});
const thMonthEL = (isActual = false) => ({
  ...thCoreEL(GH_EL, CH_EL, Z_H, "7px 8px", HEADER_COL),
  fontSize: 10, fontWeight: isActual ? 700 : 600, textAlign: "right",
  letterSpacing: "0.05em", textTransform: "uppercase", boxShadow: TH_SH_EL,
  ...(isActual ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT } : {}),
});
const thTotalEL = () => ({
  ...thMonthEL(false),
  fontWeight: 700,
  right: 0, zIndex: Z_H + 1,
  boxShadow: "-4px 0 8px -2px rgba(0,0,0,0.12), inset 0 -1px 0 #4a4a4c",
});

const tdBaseEL = (bg, align = "right", fw = 400) => ({
  boxSizing: "border-box", background: bg, textAlign: align, fontWeight: fw,
  padding: "7px 8px", fontSize: 12, color: CELL_FG, whiteSpace: "nowrap",
  verticalAlign: "middle", fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});
const tdFixedEL = (bg, fw = 400) => ({
  ...tdBaseEL(bg, "left", fw),
  position: "sticky", left: 0, zIndex: Z_S,
  width: NAME_W, minWidth: NAME_W, maxWidth: NAME_W,
  padding: "7px 14px", boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
});
const TOTAL_TOP = GH_EL + CH_EL - 1;
const tdTotalFixedEL = () => ({
  ...tdFixedEL(TOTAL_BG, 700), color: TOTAL_FG,
  position: "sticky", top: TOTAL_TOP, zIndex: Z_S + 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.08)",
});
const tdTotalScrollEL = () => ({
  ...tdBaseEL(TOTAL_BG, "right", 600), color: TOTAL_FG,
  position: "sticky", top: TOTAL_TOP, zIndex: 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
});
const tdTotalColEL = () => ({
  ...tdBaseEL(TOTAL_BG2, "right", 700),
  color: TOTAL_FG,
  position: "sticky", right: 0, top: TOTAL_TOP, zIndex: Z_S + 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "-4px 0 8px -2px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.08)",
});

/* Static table min-width — never changes when columns are hidden */
const TABLE_MIN_W = NAME_W + EL_MONTHS.length * COL_W_EL + COL_W_EL;

export default function EmployeeLabor({ filters = {}, downloadRef, setParentDownloading, actualsMonth, onHeaderMonthChange }) {
  const classes = useStyles();
  const actualThru = Math.max(0, EL_MONTHS.indexOf(actualsMonth) + 1);
  const [versions,        setVersions]        = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [version,         setVersion]         = useState(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [rows,    setRows]    = useState([]);

  /* Fetch version list; default to the latest EAC (fallback: first option) */
  useEffect(() => {
    setVersionsLoading(true);
    getIndirectVersionsAPI()
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const opts = normalizeVersions(data);
        setVersions(opts);
        if (opts.length > 0) {
          setVersion((prev) =>
            prev != null && opts.some((o) => o.id === prev)
              ? prev
              : opts[0].id,
          );
        }
      })
      .catch(() => {})
      .finally(() => setVersionsLoading(false));
  }, []);

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const f = withPeriodFiltersForDownload(filters, {
        year: String(filters.year || "2026"),
        version: versions.find((o) => o.id === version)?.label ?? "",
      });
      if (filters.department?.length) f.department = filters.department;
      if (filters.subDepartment?.length) f.subDepartment = filters.subDepartment;
      if (filters.employeeName?.length) f.employeeName = filters.employeeName;
      const { blob, filename } = await downloadEmployeeLaborAPI(f);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (_) { /* silent */ } finally { setParentDownloading?.(false); }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  const [hiddenCols,    setHiddenCols]    = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  const [colFilters,    setColFilters]    = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const [openOpKey,     setOpenOpKey]     = useState(null);
  const [dropdownPos,   setDropdownPos]   = useState({ top: 0, left: 0 });
  const filterBtnRefs    = useRef({});
  const opBtnRefs        = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);
  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current = openOpKey; }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) { const btn = filterBtnRefs.current[key]; if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC)); }
    const opKey = openOpKeyRef.current;
    if (opKey) { const b = opBtnRefs.current[opKey]; if (b) setDropdownPos(calcOpDropdownPos(b)); }
  }, []);
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => { window.removeEventListener("scroll", repositionPanels, true); window.removeEventListener("resize", repositionPanels); };
  }, [repositionPanels]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => { if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]")) setOpenFilterKey(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);
  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => { if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]")) setOpenOpKey(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    if (version == null) return;

    setLoading(true);
    setError(null);

    const selectedLabel = versions.find((o) => o.id === version)?.label ?? "";

    const f = {};
    f.version = selectedLabel;
    if (filters.year)                  f.financialYear  = String(filters.year);
    if (filters.quarter?.length)       f.quarter        = filters.quarter;
    if (filters.monthName?.length)     f.monthName      = filters.monthName;
    if (filters.department?.length)    f.department     = filters.department;
    if (filters.subDepartment?.length) f.subDepartment  = filters.subDepartment;
    if (filters.employeeName?.length)  f.employeeName   = filters.employeeName;

    // All versions go through /labor/table for now — budget endpoint disabled.
    const apiFn = getIndirectLaborTableAPI;

    apiFn(f)
      .then((res) => {
        setRows(extractRows(res));
        setLoading(false);

        const apiActualsMonth = res?.data?.actualsMonth;
        onHeaderMonthChange?.(
          Array.isArray(apiActualsMonth) && apiActualsMonth.length
            ? apiActualsMonth[apiActualsMonth.length - 1]
            : null,
        );
      })
      .catch(() => { setError("Failed to load data."); setLoading(false); });
  }, [version, versions, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Column visibility: driven by quarter filter only (month filter does NOT hide columns) */
  const colMonths   = deriveColMonths(filters);
  /* A selected global month/quarter filter overrides the table-level column
     picker — e.g. hiding Jan via the picker then selecting Jan globally
     should bring the Jan column back, not keep it hidden. */
  const visibleMonths = EL_MONTHS.filter((m) =>
    colMonths ? colMonths.has(m) : !hiddenCols.has(m),
  );
  const showTotal   = !hiddenCols.has("_total");
  const hiddenCount   = hiddenCols.size;
  const toggleCol     = (key) => setHiddenCols((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const showAllCols   = () => setHiddenCols(new Set());

  /* Close col picker on outside click */
  useEffect(() => {
    if (!colPickerOpen) return;
    const h = (e) => { if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [colPickerOpen]);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typeSort, setTypeSort] = useState(null);

  /* Map API rows → display rows (coerce to number to prevent string concat in totals) */
  const displayRows = rows
    .map((row) => ({
      n: row.employeeName || row.employee_name || row.name || "",
      v: MONTHS_LOWER.map((m) => (row[m] != null ? +row[m] : null)),
    }))
    .filter((row) => row.v.some((v) => v));

  const hasActiveFilter = (key) => {
    const f = colFilters[key];
    if (!f) return false;
    if (key === "_name") return !!(f.value && String(f.value).trim());
    return isNumFilterActive(f);
  };
  const setFilterValue = (key, value, type) => { setColFilters((prev) => ({ ...prev, [key]: { type, op: (prev[key]?.op) || "gt", value } })); setPage(1); };
  const setFilterOp    = (key, op)           => { setColFilters((prev) => ({ ...prev, [key]: { ...(prev[key] || { type: "num", value: "" }), op } })); };
  const clearFilter    = (key)               => { setColFilters((prev) => { const n = { ...prev }; delete n[key]; return n; }); setPage(1); };
  const toggleFilter = (key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key); setOpenOpKey(null);
  };
  const toggleOpDropdown = (key) => {
    if (openOpKey === key) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[key];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };

  const getFilterLabel = (key) => {
    if (key === "_name") return "Employee Name";
    if (key === "_total") return "Total";
    return key;
  };

  const renderFilterContent = (key) => {
    const isText = key === "_name";
    const filter = colFilters[key] || { type: isText ? "text" : "num", op: "gt", value: "" };
    if (isText) {
      return (
        <TextFilterContent
          label={getFilterLabel(key)}
          value={filter.value}
          onChange={(v) => setFilterValue(key, v, "text")}
          onClear={() => clearFilter(key)}
          placeholder="Filter…"
        />
      );
    }
    return (
      <NumericFilterContent
        label={getFilterLabel(key)}
        value={filter.value}
        op={filter.op || "gt"}
        onValueChange={(v) => setFilterValue(key, v, "num")}
        onClear={() => clearFilter(key)}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  useEffect(() => { setPage(1); }, [rows]);

  /* Total column: monthName filter → sum only those months;
     quarter filter → sum that quarter's months;
     no filter → sum all 12 (initial behavior untouched). */
  const totalMonths = deriveTotalMonths(filters);
  const rowTotal = (v) =>
    totalMonths
      ? EL_MONTHS.reduce((s, m, i) => (totalMonths.has(m) ? s + (+v[i] || 0) : s), 0)
      : v.reduce((s, x) => s + (+x || 0), 0);

  const filteredRows = displayRows.filter((row) => {
    for (const [key, filter] of Object.entries(colFilters)) {
      if (!hasActiveFilter(key)) continue;
      if (key === "_name") {
        if (!row.n.toLowerCase().includes(filter.value.toLowerCase())) return false;
      } else if (key === "_total") {
        if (!applyNumFilter(rowTotal(row.v), filter)) return false;
      } else {
        const idx = EL_MONTHS.indexOf(key);
        if (idx !== -1 && !applyNumFilter(row.v[idx], filter)) return false;
      }
    }
    return true;
  });

  const sortedRows = typeSort === null
    ? filteredRows
    : [...filteredRows].sort((a, b) =>
        typeSort === "asc"
          ? a.n.localeCompare(b.n)
          : b.n.localeCompare(a.n),
      );

  const totalPages  = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage    = Math.min(page, totalPages);
  const pagedRows   = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const ps = new Set([1, totalPages, safePage, safePage - 1, safePage + 1, safePage - 2, safePage + 2]);
    return [...ps].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  })();
  const pgBtn = (active) => ({
    minWidth: 30, height: 28, padding: "0 6px", border: `1px solid ${active ? TH_BG_BAND : "#E2E8F0"}`,
    borderRadius: 6, background: active ? TH_BG_BAND : "#fff", fontSize: 12,
    color: active ? "#fff" : TH_BG_BAND, cursor: "pointer", fontFamily: FONT_EL,
    display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, fontWeight: active ? 600 : 400,
  });

  /* Per-month totals across visible rows (recalculated when table filters are active) */
  const hasTableFilters = Object.entries(colFilters).some(([key]) => hasActiveFilter(key));
  const rowsForTotal = hasTableFilters ? filteredRows : displayRows;
  const totalV = MONTHS_LOWER.map((_, mi) =>
    rowsForTotal.reduce((s, r) => s + (r.v[mi] ?? 0), 0)
  );
  const grandTotal = rowTotal(totalV);

  const fyYear = filters.year || new Date().getFullYear();

  return (
    <div>
      {/* Filter bar */}
      <div className={classes.svbFilters} style={{ marginBottom: "14px" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div className={classes.svbFilterGroup}>
            <span className={classes.svbFilterLabel}>View</span>
            <select
              className={classes.svbFilterSelect}
              value={version ?? ""}
              disabled={versionsLoading || versions.length === 0}
              onChange={(e) => {
                const v = e.target.value;
                setVersion(isNaN(Number(v)) ? v : Number(v));
              }}
            >
              {versionsLoading && <option value="">Loading…</option>}
              {!versionsLoading && versions.length === 0 && <option value="">—</option>}
              {versions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Column picker */}
        <div className={classes.colPickerWrap} ref={colPickerRef}>
          <button
            className={`${classes.colsBtn} ${colPickerOpen ? classes.colsBtnActive : ""}`}
            onClick={() => setColPickerOpen((p) => !p)}
          >
            <ColsIcon />
            Columns
            {hiddenCount > 0 && <span className={classes.hiddenBadge}>{hiddenCount} Hidden</span>}
          </button>
          {colPickerOpen && (
            <div className={classes.colPicker}>
              <div className={classes.colPickerHeader}>
                <span className={classes.colPickerTitle}>Show / Hide</span>
                {hiddenCount > 0 && <button className={classes.colPickerReset} onClick={showAllCols}>Show All</button>}
              </div>
              <div className={classes.colPickerSection}>Months</div>
              {EL_MONTHS.map((m) => (
                <label key={m} className={classes.colPickerItem} style={!!colMonths && !colMonths.has(m) ? { opacity: 0.45 } : undefined}>
                  <input type="checkbox" className={classes.colPickerCheck}
                    checked={colMonths ? colMonths.has(m) : !hiddenCols.has(m)}
                    disabled={!!colMonths}
                    onChange={() => toggleCol(m)} />
                  {m}
                </label>
              ))}
              <div className={classes.colPickerSection}>Other</div>
              <label className={classes.colPickerItem}>
                <input type="checkbox" className={classes.colPickerCheck} checked={!hiddenCols.has("_total")} onChange={() => toggleCol("_total")} />
                Total
              </label>
            </div>
          )}
        </div>
      </div>

      {loading || versionsLoading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: "8px 0 12px", fontFamily: FONT_EL, fontSize: 13, color: "#DC2626" }}>{error}</div>
      ) : (

      /* Table */
      <div className={classes.tableCard}>
        <div className={classes.tableBody}>
        <div className={classes.tableWrap}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", fontFamily: FONT_EL, fontSize: 12, width: "100%", minWidth: TABLE_MIN_W }}>
            <colgroup>
              <col style={{ width: NAME_W }} />
              {visibleMonths.map((m) => <col key={m} style={{ width: COL_W_EL }} />)}
              {showTotal && <col style={{ width: COL_W_EL }} />}
            </colgroup>
            <thead style={{ background: TH_BG }}>
              <tr style={{ height: GH_EL }}>
                <th style={thFixedBlankEL()} aria-hidden />
                <th colSpan={visibleMonths.length + (showTotal ? 1 : 0)} style={thBandGroupEL()}>
                  FY-{fyYear}
                </th>
              </tr>
              <tr style={{ height: CH_EL }}>
                <th style={thFixedColEL()}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                    <span style={{ color: openFilterKey === "_name" ? "#60A5FA" : undefined }}>Employee Name</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <button
                        className={`${classes.filterIconBtn} ${typeSort ? classes.filterIconBtnActive : ""}`}
                        onClick={() => setTypeSort((s) => s === null ? "asc" : s === "asc" ? "desc" : null)}
                        title={typeSort === "asc" ? "Sorted A→Z (click for Z→A)" : typeSort === "desc" ? "Sorted Z→A (click to clear)" : "Sort by Name"}
                        style={{ fontSize: 11, width: 18, height: 16 }}
                      >
                        {typeSort === "asc" ? "↑" : typeSort === "desc" ? "↓" : "↕"}
                      </button>
                      <button ref={(el) => { filterBtnRefs.current["_name"] = el; }} data-filter-btn
                        className={`${classes.filterIconBtn} ${hasActiveFilter("_name") ? classes.filterIconBtnActive : ""}`}
                        onClick={() => toggleFilter("_name")} title="Filter Employee Name">
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </div>
                </th>
                {visibleMonths.map((m) => {
                  const isActualM = EL_MONTHS.indexOf(m) < actualThru;
                  return (
                  <th key={m} style={thMonthEL(isActualM)}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                      <span style={{ color: openFilterKey === m ? "#60A5FA" : undefined }}>{m}</span>
                      <button ref={(el) => { filterBtnRefs.current[m] = el; }} data-filter-btn
                        className={`${classes.filterIconBtn} ${hasActiveFilter(m) ? classes.filterIconBtnActive : ""}`}
                        onClick={() => toggleFilter(m)} title={`Filter ${m}`}
                        style={isActualM ? { color: TH_ACTUAL_TEXT, opacity: 1 } : undefined}
                        >
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                  );
                })}
                {showTotal && (
                  <th style={thTotalEL()}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                      <span style={{ color: openFilterKey === "_total" ? "#60A5FA" : undefined }}>Total</span>
                      <button ref={(el) => { filterBtnRefs.current["_total"] = el; }} data-filter-btn
                        className={`${classes.filterIconBtn} ${hasActiveFilter("_total") ? classes.filterIconBtnActive : ""}`}
                        onClick={() => toggleFilter("_total")} title="Filter Total">
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {/* Grand total row */}
              <tr style={{ height: RH_EL }}>
                <td style={tdTotalFixedEL()}>Total</td>
                {visibleMonths.map((m) => {
                  const mi = EL_MONTHS.indexOf(m);
                  const val = totalV[mi];
                  return (
                    <td key={m} style={{ ...tdTotalScrollEL(), color: val < 0 ? "#DC2626" : TOTAL_FG }}>
                      {fmtEL(val)}
                    </td>
                  );
                })}
                {showTotal && <td style={tdTotalColEL()}>{fmtEL(grandTotal)}</td>}
              </tr>

              {/* Data rows */}
              {pagedRows.map((row, i) => {
                const total = rowTotal(row.v);
                const bg = ((safePage - 1) * pageSize + i) % 2 === 0 ? EVEN_BG : ODD_BG;
                return (
                  <tr key={i} style={{ height: RH_EL }}>
                    <td style={tdFixedEL(bg)}>{row.n}</td>
                    {visibleMonths.map((m) => {
                      const mi = EL_MONTHS.indexOf(m);
                      const val = row.v[mi];
                      return (
                        <td key={m} style={{ ...tdBaseEL(bg), color: val !== null && val < 0 ? "#DC2626" : CELL_FG }}>
                          {fmtEL(val)}
                        </td>
                      );
                    })}
                    {showTotal && (
                      <td style={{ ...tdBaseEL("#F8FAFC"), color: total < 0 ? "#DC2626" : CELL_FG, fontWeight: 600, position: "sticky", right: 0, zIndex: Z_S, boxShadow: "-4px 0 8px -2px rgba(0,0,0,0.08)" }}>
                        {fmtEL(total)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination bar */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 20px", borderTop:"1px solid #E2E8F0", background:"#FAFAFA", gap:12, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, color:"#94A3B8", fontFamily:FONT_EL, whiteSpace:"nowrap" }}>
            {sortedRows.length}{sortedRows.length !== displayRows.length ? ` of ${displayRows.length}` : ""} row{sortedRows.length !== 1 ? "s" : ""}
          </span>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:12, color:"#94A3B8", fontFamily:FONT_EL, whiteSpace:"nowrap" }}>Rows Per Page</span>
            <select style={{ height:28, border:"1px solid #E2E8F0", borderRadius:6, fontSize:12, color:"#374151", padding:"0 6px", background:"#fff", cursor:"pointer", outline:"none", fontFamily:FONT_EL }}
              value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10, 25, 50, 100, ...(displayRows.length > 100 ? [displayRows.length] : [])].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {totalPages > 1 && (
              <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                <button style={pgBtn(false)} disabled={safePage === 1} onClick={() => setPage(1)}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7,2 3,6 7,10"/><polyline points="11,2 7,6 11,10"/></svg>
                </button>
                <button style={pgBtn(false)} disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="8,2 4,6 8,10"/></svg>
                </button>
                {pageNumbers.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && pageNumbers[i - 1] < p - 1 && <span style={{ fontSize:12, color:"#94A3B8", padding:"0 4px", userSelect:"none" }}>…</span>}
                    <button style={pgBtn(p === safePage)} onClick={() => setPage(p)}>{p}</button>
                  </React.Fragment>
                ))}
                <button style={pgBtn(false)} disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4,2 8,6 4,10"/></svg>
                </button>
                <button style={pgBtn(false)} disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="5,2 9,6 5,10"/><polyline points="1,2 5,6 1,10"/></svg>
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
      )}

      {openFilterKey && createPortal(
        <div data-filter-panel className={classes.filterPanel} style={{ top: filterPanelPos.top, left: filterPanelPos.left }}>
          {renderFilterContent(openFilterKey)}
        </div>,
        document.body
      )}
      {openOpKey && createPortal(
        <OpDropdownContent
          activeOp={(colFilters[openOpKey]?.op) || "gt"}
          onSelect={(op) => { setFilterOp(openOpKey, op); setOpenOpKey(null); }}
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        />,
        document.body
      )}
    </div>
  );
}
